import {
    type ReactNode
} from "react";
import {
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    LineChart,
    Separator,
    Progress,
    Button,
    Chart,
    Card,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    type LucideIcon,
    Thermometer,
    CloudRain,
    CloudSun,
    Ellipsis,
    Sunrise,
    Droplet,
    MapPin,
    Cloud,
    Plus,
    Wind,
    Moon,
    Sun
} from "lucide-react-native";
import {
    LinearGradient,
    Circle,
    Defs,
    Line,
    Rect,
    Path,
    Stop,
    Svg,
    G
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

type Kind = "sun" | "cloudSun" | "cloud" | "moon" | "rain";

type Hour = {
    time?: string;
    temp: number;
    kind: Kind;
};

type Day = {
    offset: number;
    min: number;
    max: number;
    kind: Kind;
};

const PlusIcon = createKitIcon(Plus);

const MoreIcon = createKitIcon(Ellipsis);

const KIND_ICONS: Record<Kind, LucideIcon> = {
    cloudSun: CloudSun,
    rain: CloudRain,
    cloud: Cloud,
    moon: Moon,
    sun: Sun
};

const HOURS: Array<Hour> = [
    {
        kind: "cloudSun",
        temp: 18
    },
    {
        time: "15:00",
        kind: "sun",
        temp: 19
    },
    {
        kind: "cloudSun",
        time: "16:00",
        temp: 20
    },
    {
        kind: "cloud",
        time: "17:00",
        temp: 20
    },
    {
        time: "18:00",
        kind: "moon",
        temp: 19
    },
    {
        time: "19:00",
        kind: "moon",
        temp: 17
    },
    {
        kind: "cloud",
        time: "20:00",
        temp: 16
    }
];

const DAYS: Array<Day> = [
    {
        kind: "cloudSun",
        offset: 0,
        min: 14,
        max: 21
    },
    {
        kind: "cloud",
        offset: 1,
        min: 13,
        max: 19
    },
    {
        kind: "cloud",
        offset: 2,
        min: 11,
        max: 16
    },
    {
        kind: "rain",
        offset: 3,
        min: 10,
        max: 14
    },
    {
        kind: "rain",
        offset: 4,
        max: 13,
        min: 9
    },
    {
        kind: "cloudSun",
        offset: 5,
        min: 10,
        max: 15
    },
    {
        kind: "sun",
        offset: 6,
        min: 12,
        max: 18
    }
];

const NOW_DATE = new Date(2026, 10, 1, 14, 5);

const CURRENT_TEMP = 18;

const SCALE_MIN = 8;

const SCALE_MAX = 22;

const UV_VALUE = 3;

const UV_MAX = 11;

const SUN_RAYS = [
    {
        x1: 76,
        x2: 76,
        y1: 6,
        y2: 14
    },
    {
        x1: 76,
        x2: 76,
        y1: 62,
        y2: 70
    },
    {
        x1: 48,
        x2: 56,
        y1: 38,
        y2: 38
    },
    {
        x2: 104,
        x1: 96,
        y1: 38,
        y2: 38
    },
    {
        x1: 56,
        x2: 62,
        y1: 18,
        y2: 24
    },
    {
        x1: 96,
        x2: 90,
        y1: 18,
        y2: 24
    },
    {
        x1: 96,
        x2: 90,
        y1: 58,
        y2: 52
    }
];

const CLOUD_CIRCLES = [
    {
        cx: 40,
        cy: 84,
        r: 20
    },
    {
        cx: 68,
        cy: 76,
        r: 26
    },
    {
        cx: 94,
        cy: 88,
        r: 16
    }
];

const COMPASS_TICKS = [
    {
        x1: 24,
        x2: 24,
        y1: 3,
        y2: 7
    },
    {
        x1: 45,
        x2: 41,
        y1: 24,
        y2: 24
    },
    {
        x1: 24,
        x2: 24,
        y1: 45,
        y2: 41
    },
    {
        y1: 24,
        x1: 3,
        x2: 7,
        y2: 24
    }
];

const stylesheet = StyleSheet.create({
    heroTop: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    split: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    hero: {
        flexDirection: "column"
    },
    artWrap: {
        alignSelf: "flex-start"
    },
    column: {
        flexDirection: "column"
    },
    end: {
        alignItems: "flex-end"
    },
    ground: {
        position: "absolute",
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    day: {
        alignItems: "center",
        flexDirection: "row"
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    tiles: {
        flexDirection: "row"
    },
    hour: {
        alignItems: "center"
    },
    grow: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const Weather01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        avatarSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const weekday = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long"
    });

    const shortWeekday = new Intl.DateTimeFormat(activeLocale, {
        weekday: "short"
    });

    const clock = new Intl.DateTimeFormat(activeLocale, {
        minute: "2-digit",
        hour: "2-digit",
        hour12: false
    });

    const hourWidth = avatarSizes.small + spaces.spacingSm;

    const artSize = avatarSizes.xLarge + spaces.spacingXl;

    const rangeHeight = spaces.spacingMd;

    const barHeight = spaces.spacingSm;

    const getKindColor = (kind: Kind) => {
        if(kind === "rain") {
            return colors.chart.categorical["3"];
        }

        if(kind === "cloud" || kind === "moon") {
            return colors.content.icon.mid;
        }

        return colors.chart.categorical["4"];
    };

    const renderKindIcon = (kind: Kind, size: "small" | "large") => <Icon
        customColor={getKindColor(kind)}
        icon={KIND_ICONS[kind]}
        size={size}
    />;

    const renderHour = (hour: Hour, index: number) => <View
        style={[
            stylesheet.hour,
            {
                gap: spaces.spacingSm,
                width: hourWidth
            }
        ]}
        key={hour.time ?? "now"}
    >
        <Text
            variant={index === 0 ? "labelSmallSize" : "bodySmallSize"}
            color={index === 0 ? "high" : "mid"}
        >
            {hour.time ?? t("blocks.weather01.now")}
        </Text>
        {renderKindIcon(hour.kind, "large")}
        <Text
            variant="titleSmallSize"
            color="high"
        >
            {`${hour.temp}°`}
        </Text>
    </View>;

    const renderDay = (day: Day) => {
        const isToday = day.offset === 0;

        const left = (day.min - SCALE_MIN) / (SCALE_MAX - SCALE_MIN) * 100;

        const width = (day.max - day.min) / (SCALE_MAX - SCALE_MIN) * 100;

        const marker = (CURRENT_TEMP - SCALE_MIN) / (SCALE_MAX - SCALE_MIN) * 100;

        const date = new Date(NOW_DATE.getFullYear(), NOW_DATE.getMonth(), NOW_DATE.getDate() + day.offset);

        return <View
            style={stylesheet.column}
            key={day.offset}
        >
            <Separator/>
            <View
                style={[
                    stylesheet.day,
                    {
                        paddingVertical: spaces.spacingSm,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={{
                        width: avatarSizes.medium + spaces.spacingSm
                    }}
                >
                    <Text
                        variant={isToday ? "labelMediumSize" : "bodyMediumSize"}
                        color={isToday ? "high" : "mid"}
                        numberOfLines={1}
                    >
                        {isToday ? t("blocks.weather01.today") : shortWeekday.format(date)}
                    </Text>
                </View>
                {renderKindIcon(day.kind, "large")}
                <View
                    style={{
                        width: avatarSizes.xxSmall + spaces.spacingXs
                    }}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {`${day.min}°`}
                    </Text>
                </View>
                <View
                    style={stylesheet.grow}
                >
                    <Svg
                        accessibilityLabel={t("blocks.weather01.rangeLabel", [
                            String(day.min),
                            String(day.max)
                        ])}
                        height={rangeHeight}
                        width="100%"
                    >
                        <Defs>
                            <LinearGradient
                                id={`range-${day.offset}`}
                                x2="100%"
                                x1="0%"
                                y1="0%"
                                y2="0%"
                            >
                                <Stop
                                    stopColor={colors.chart.categorical["3"]}
                                    offset="0%"
                                />
                                <Stop
                                    stopColor={colors.chart.categorical["2"]}
                                    offset="100%"
                                />
                            </LinearGradient>
                        </Defs>
                        <Rect
                            fill={colors.content.container.mid}
                            y={(rangeHeight - barHeight) / 2}
                            height={barHeight}
                            rx={barHeight / 2}
                            width="100%"
                        />
                        <Rect
                            fill={`url(#range-${day.offset})`}
                            y={(rangeHeight - barHeight) / 2}
                            width={`${width}%`}
                            height={barHeight}
                            rx={barHeight / 2}
                            x={`${left}%`}
                        />
                        {
                            isToday ?
                                <Circle
                                    r={rangeHeight / 2 - borders.chartLine}
                                    fill={colors.content.container.default}
                                    stroke={colors.content.text.high}
                                    strokeWidth={borders.chartLine}
                                    cy={rangeHeight / 2}
                                    cx={`${marker}%`}
                                />
                            :
                                null
                        }
                    </Svg>
                </View>
                <View
                    style={{
                        width: avatarSizes.xxSmall + spaces.spacingXs
                    }}
                >
                    <Text
                        variant="labelMediumSize"
                        color="high"
                    >
                        {`${day.max}°`}
                    </Text>
                </View>
            </View>
        </View>;
    };

    const renderTile = (key: string, icon: LucideIcon, children: ReactNode) => <Card
        style={stylesheet.grow}
        variant="outline"
        key={key}
    >
        <Card.Header>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Icon
                    icon={icon}
                    color="mid"
                />
                <Card.Title
                    headingLevel={2}
                >
                    {t(`blocks.weather01.tiles.${key}`)}
                </Card.Title>
            </View>
        </Card.Header>
        <Card.Content>
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {children}
            </View>
        </Card.Content>
    </Card>;

    const renderValue = (value: string, unit?: string) => <Text
        variant="headlineMediumSize"
        color="high"
    >
        {value}
        {
            unit ?
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {` ${unit}`}
                </Text>
            :
                null
        }
    </Text>;

    const lineRows = HOURS.map((hour) => ({
        time: hour.time ?? t("blocks.weather01.now"),
        temp: hour.temp
    }));

    return <ScrollView
        contentContainerStyle={[
            stylesheet.screen,
            {
                maxWidth: shapeConfigs.sheetMaxWidth
            }
        ]}
    >
        <View
            style={[
                stylesheet.hero,
                {
                    padding: spaces.spacingMd,
                    paddingBottom: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Svg
                preserveAspectRatio="none"
                style={stylesheet.ground}
                height="100%"
                width="100%"
            >
                <Defs>
                    <LinearGradient
                        y2="100%"
                        id="sky"
                        x1="0%"
                        x2="0%"
                        y1="0%"
                    >
                        <Stop
                            stopColor={colors.chart.categoricalArea["3"]}
                            offset="0%"
                        />
                        <Stop
                            stopColor={colors.chart.categoricalArea["3"]}
                            stopOpacity={0}
                            offset="100%"
                        />
                    </LinearGradient>
                </Defs>
                <Rect
                    fill="url(#sky)"
                    height="100%"
                    width="100%"
                />
            </Svg>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Icon
                    icon={MapPin}
                    color="high"
                />
                <View
                    style={stylesheet.grow}
                >
                    <Text
                        variant="titleLargeSize"
                        role="heading"
                        color="high"
                    >
                        {t("blocks.weather01.city")}
                    </Text>
                </View>
                <Button
                    accessibilityLabel={t("blocks.weather01.addCity")}
                    onPress={handlePress}
                    icon={PlusIcon}
                    variant="ghost"
                    type="neutral"
                />
                <Button
                    accessibilityLabel={t("blocks.weather01.more")}
                    onPress={handlePress}
                    icon={MoreIcon}
                    variant="ghost"
                    type="neutral"
                />
            </View>
            <View
                style={stylesheet.heroTop}
            >
                <View
                    style={stylesheet.grow}
                >
                    <Text
                        accessibilityLabel={t("blocks.weather01.degrees", [
                            String(CURRENT_TEMP)
                        ])}
                        variant="displayLargeSize"
                        color="high"
                    >
                        {`${CURRENT_TEMP}°`}
                    </Text>
                    <Text
                        variant="titleLargeSize"
                        color="high"
                    >
                        {t("blocks.weather01.condition")}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.weather01.range", [
                            "21",
                            "14",
                            `${weekday.format(NOW_DATE)} ${clock.format(NOW_DATE)}`
                        ])}
                    </Text>
                </View>
                <View
                    style={stylesheet.artWrap}
                >
                    <Svg
                        accessibilityLabel={t("blocks.weather01.conditionIcon")}
                        viewBox="0 0 120 120"
                        height={artSize}
                        width={artSize}
                    >
                        <G
                            stroke={colors.chart.categorical["4"]}
                            strokeLinecap="round"
                            strokeWidth={4}
                        >
                            {
                                SUN_RAYS.map((ray) => <Line
                                    key={`${ray.x1}-${ray.y1}`}
                                    x1={ray.x1}
                                    x2={ray.x2}
                                    y1={ray.y1}
                                    y2={ray.y2}
                                />)
                            }
                        </G>
                        <Circle
                            fill={colors.chart.categorical["4"]}
                            cx={76}
                            cy={38}
                            r={22}
                        />
                        <G
                            stroke={colors.content.border.default}
                            fill={colors.content.container.raised}
                            strokeWidth={1}
                        >
                            {
                                CLOUD_CIRCLES.map((circle) => <Circle
                                    key={`${circle.cx}-${circle.cy}`}
                                    cx={circle.cx}
                                    cy={circle.cy}
                                    r={circle.r}
                                />)
                            }
                            <Rect
                                height={20}
                                width={76}
                                rx={10}
                                x={30}
                                y={84}
                            />
                        </G>
                        <G
                            fill={colors.content.container.raised}
                        >
                            {
                                CLOUD_CIRCLES.map((circle) => <Circle
                                    key={`fill-${circle.cx}-${circle.cy}`}
                                    r={circle.r - 1}
                                    cx={circle.cx}
                                    cy={circle.cy}
                                />)
                            }
                            <Rect
                                height={18}
                                width={74}
                                rx={9}
                                x={31}
                                y={85}
                            />
                        </G>
                    </Svg>
                </View>
            </View>
        </View>
        <View
            style={[
                stylesheet.column,
                {
                    paddingHorizontal: spaces.spacingMd,
                    paddingBottom: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <Card
                variant="outline"
            >
                <Card.Header>
                    <Card.Title>
                        {t("blocks.weather01.hourly")}
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    <ScrollView
                        showsHorizontalScrollIndicator={false}
                        horizontal={true}
                    >
                        {HOURS.map(renderHour)}
                    </ScrollView>
                </Card.Content>
            </Card>
            <Chart
                config={{
                    temp: {
                        title: t("blocks.weather01.temperature"),
                        colorIndex: 2
                    }
                }}
                title={t("blocks.weather01.curveTitle")}
                spreadBehaviour="stretch"
                isAnimatedOnMount={true}
                size="small"
            >
                <LineChart
                    summary={t("blocks.weather01.curveSummary")}
                    categoryTitle={t("blocks.weather01.time")}
                    valueFormatter={(value) => `${value}°`}
                    categoryKey="time"
                    plotSize="small"
                    data={lineRows}
                    curve="natural"
                    seriesKeys={[
                        "temp"
                    ]}
                />
            </Chart>
            <Card
                variant="outline"
            >
                <Card.Header>
                    <Card.Title>
                        {t("blocks.weather01.weekly")}
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    {DAYS.map(renderDay)}
                </Card.Content>
            </Card>
            <View
                style={[
                    stylesheet.tiles,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    renderTile("feels", Thermometer, <>
                        {renderValue("17°")}
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.weather01.feelsNote")}
                        </Text>
                    </>)
                }
                {
                    renderTile("humidity", Droplet, <>
                        {renderValue(new Intl.NumberFormat(activeLocale, {
                            style: "percent"
                        }).format(0.72))}
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.weather01.humidityNote")}
                        </Text>
                    </>)
                }
            </View>
            <View
                style={[
                    stylesheet.tiles,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    renderTile("wind", Wind, <View
                        style={stylesheet.split}
                    >
                        <View
                            style={stylesheet.grow}
                        >
                            {renderValue("14", t("blocks.weather01.kmh"))}
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.weather01.windNote")}
                            </Text>
                        </View>
                        <Svg
                            accessibilityLabel={t("blocks.weather01.windLabel")}
                            height={avatarSizes.medium}
                            width={avatarSizes.medium}
                            viewBox="0 0 48 48"
                        >
                            <Circle
                                stroke={colors.content.border.default}
                                strokeWidth={1.5}
                                fill="none"
                                cx={24}
                                cy={24}
                                r={22}
                            />
                            <G
                                stroke={colors.content.icon.low}
                                strokeLinecap="round"
                                strokeWidth={1.5}
                            >
                                {
                                    COMPASS_TICKS.map((tick) => <Line
                                        key={`${tick.x1}-${tick.y1}`}
                                        x1={tick.x1}
                                        x2={tick.x2}
                                        y1={tick.y1}
                                        y2={tick.y2}
                                    />)
                                }
                            </G>
                            <G
                                transform="rotate(45 24 24)"
                            >
                                <Path
                                    d="M24 9 L30 30 L24 26 L18 30 Z"
                                    fill={colors.content.text.high}
                                />
                            </G>
                        </Svg>
                    </View>)
                }
                {
                    renderTile("uv", Sun, <>
                        {renderValue(String(UV_VALUE), t("blocks.weather01.uvLevel"))}
                        <Progress
                            accessibilityLabel={t("blocks.weather01.uvLabel", [
                                String(UV_VALUE),
                                String(UV_MAX)
                            ])}
                            value={UV_VALUE}
                            size="small"
                            max={UV_MAX}
                        />
                    </>)
                }
            </View>
            <View
                style={stylesheet.tiles}
            >
                {
                    renderTile("sunrise", Sunrise, <View
                        style={[
                            stylesheet.split,
                            stylesheet.end
                        ]}
                    >
                        <View>
                            {renderValue("06:58")}
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.weather01.sunsetNote", [
                                    "17:49"
                                ])}
                            </Text>
                        </View>
                        <Svg
                            accessibilityLabel={t("blocks.weather01.sunLabel")}
                            width={avatarSizes.xLarge + spaces.spacingXxl}
                            height={avatarSizes.small + spaces.spacingXs}
                            viewBox="0 0 160 56"
                        >
                            <Line
                                stroke={colors.content.border.default}
                                strokeWidth={1.5}
                                x2={160}
                                y1={48}
                                y2={48}
                                x1={0}
                            />
                            <Path
                                stroke={colors.chart.categorical["4"]}
                                d="M8 48 C 34 -8, 126 -8, 152 48"
                                strokeLinecap="round"
                                strokeDasharray="3 4"
                                strokeWidth={2}
                                fill="none"
                            />
                            <Path
                                stroke={colors.chart.categorical["4"]}
                                d="M8 48 C 20 20, 40 6, 70 6"
                                strokeLinecap="round"
                                strokeWidth={2.5}
                                fill="none"
                            />
                            <Circle
                                stroke={colors.content.container.default}
                                fill={colors.chart.categorical["4"]}
                                strokeWidth={2}
                                cx={70}
                                cy={6}
                                r={6}
                            />
                        </Svg>
                    </View>)
                }
            </View>
        </View>
    </ScrollView>;
};
export default Weather01;
